// Workspace-owned org-selection state — moved verbatim from
// `src/container/organization/atoms/organizationAtoms.tsx`. R2 CURATED
// (ARCFE-985): the raw WRITABLE atom no longer reaches the `/data` door —
// reads via `useSelectedOrganizationId`, writes via the
// `setSelectedOrganizationId` command (which preserves the full
// `SetStateActionWithReset` surface incl. `RESET` and functional updates).
// Test arrangement imports the writable atom through
// `@heidi/workspace/testing`.

import { getDefaultStore, type WritableAtom } from 'jotai';
// eslint-disable-next-line no-restricted-imports -- package source can't import the app-layer '@/atoms/atomWithHash' guard; the custom setHash below carries the same try/catch
import { atomWithHash } from 'jotai-location';
import { type RESET } from 'jotai/utils';

// Mirrors jotai-location's (unexported) SetStateActionWithReset — an explicit
// annotation so the standalone package build doesn't infer a type referencing
// jotai-location's internal dist paths (TS2742).
type SetStateActionWithReset<Value> =
  | Value
  | typeof RESET
  | ((prev: Value) => Value | typeof RESET);

// Guarded replaceState — STOP-GAP for an upstream Intercom bug (reported to
// Intercom support): their messenger wraps `history.replaceState` and throws
// once its iframe is torn down. jotai-location writes the atom's state
// before this runs, so a swallowed throw loses only the URL fragment update —
// unguarded it crashes the React tree mid-commit. The app tree uses
// `@/atoms/atomWithHash` for the same guard; this package cannot import it.
// Safe to keep after Intercom fixes frame.js.
const setHashWithGuardedReplace = (searchParams: string) => {
  // During client-side route transitions Next.js may momentarily leave
  // window.location.pathname as the unresolved page template (e.g.
  // `/scribe/session/[...id]`). A `[` is never valid in a resolved URL
  // path, so bail out to avoid clobbering the URL with the raw template.
  if (window.location.pathname.includes('[')) return;

  try {
    const newUrl = `${window.location.pathname}${window.location.search}${
      searchParams ? `#${searchParams}` : ''
    }`;
    window.history.replaceState(window.history.state, '', newUrl);
  } catch (error) {
    console.warn(
      '[selectedOrganizationIdAtom] URL hash update failed (a third-party history wrapper threw); atom state is unaffected.',
      error
    );
  }
};

export const selectedOrganizationIdAtom: WritableAtom<
  string | null,
  [SetStateActionWithReset<string | null>],
  void
> = atomWithHash<string | null>('selectedOrganizationId', null, {
  setHash: setHashWithGuardedReplace,
});

/** Non-React read of the selected organization id (default store). */
export const getSelectedOrganizationId = (): string | null =>
  getDefaultStore().get(selectedOrganizationIdAtom);

/**
 * Setter command (default store). Accepts everything the atom's own setter
 * does — a value, `RESET`, or a functional update — so the legacy `useAtom`
 * pair's write half maps 1:1.
 */
export const setSelectedOrganizationId = (
  value: SetStateActionWithReset<string | null>
): void => {
  getDefaultStore().set(selectedOrganizationIdAtom, value);
};
